import { action, computed, observable, autorun, reaction } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';
import { throttle } from 'throttle-debounce';
import {
  RFQQuery,
  CreateRFQMutation,
  FileRequirementsQuery,
} from 'apps/rfq/gql';
import { JobSpecAppManager } from 'apps/jobSpec/appManager';
import {
  JobSpecModel,
  RFQApp,
  JobSpecApp,
  OptionValueModel,
} from 'apps/rfq/app';
import { AppManager, Input } from 'core';
import { routerStore } from 'core/routing';
import { analytics } from 'core/analytics';
import FileAppManager from 'apps/file/appManager';

import { FormStore } from 'libs/forms/app';

import { getFormatObject } from 'libs/rendering/utils';

import { PartExtractionManager } from 'apps/conversions/appManager';
import { clientStore } from 'apps/client/app';

import { modalStore } from 'apps/modals/app';
import { fromSessionStorage, toSessionStorage } from 'core/localStorage';
import SignUpModal from 'apps/profile/signUp/modal';

import { ValidateRFQInputManager } from './validateRfqInputManager';

const addressConfigForRFQForm = {
  addressForm: {
    fields: ['address'],
    styles: {
      formColumns: 1,
      labelBold: true,
    },
  },
  title: 'Order Details.',
  fields: {
    address: {
      label: 'Shipping Address',
      placeholder: 'Choose an Address',
      component: 'addresses.AddressChoiceField', // what to do if no addresses have been added - problem with adding an address and selecting it at the same time
      noValidation: true,
      oneColumn: true,
      optional: true,
    },
  },
};

export default class CreateRFQAppManager extends AppManager {
  static registryName = 'CreateRFQAppManager';
  fileId = null;
  file = null;

  @observable addressFormStore = {};

  constructor(parent, { viewerControl = null }, options) {
    super(parent, null, options);

    this.viewerControl = viewerControl;

    this.AppRef('rfqApp', RFQApp);

    // why branch here when all the same attributes could in theory be used in an 'edit' context
    this.rfqApp = new RFQApp(this);
    this.rfqApp.jobSpec = new JobSpecApp(this.rfqApp, {
      // rfq: this.rfqApp,
    });

    this.jobSpecManager = new JobSpecAppManager(this, {
      jobSpecApp: this.rfqApp.jobSpec,
    });

    this.LoadRFQFormFromStorage();

    // if we just came back to this url, try to autosubmit this form created as anonymous user
    if (
      sessionStorage.getItem('pendingRFQSubmission') ===
      routerStore.route.params.sharedFileId
    ) {
      // need to check for file because there is potentially an initial construction
      // with file === null
      if (
        this.jobSpecManager.isValid &&
        clientStore.signedInProfile &&
        this.fileId
      ) {
        this.SubmitRFQ();
      }
    }

    this.addressFormStore = new FormStore(
      this.rfqApp,
      addressConfigForRFQForm,
      'addressForm',
    );

    this.initFileRequirementsReaction();
    this.initRFQFormSerializationReaction();
  }

  @action cloneRfqById = id => {
    const cloneRfqQuery = new RFQQuery(this, {
      variables: {
        id,
      },
      fetchPolicy: 'network-only',
      queryKey: 'cloneRfqQuery',
    });
    cloneRfqQuery.process(
      rfqData => rfqData,
      rfqData => {
        if (rfqData) {
          this.cloneRfqDataIntoFormTarget(rfqData);
          cloneRfqQuery.disposer();
        }
      },
    );
    cloneRfqQuery.query();
  };

  @action cloneRfqDataIntoFormTarget = async rfqData => {
    if (!rfqData || !rfqData.jobSpec) return;

    const { jobSpec } = rfqData;
    const loadingJobSpec = new JobSpecApp(this.rfqApp, jobSpec);
    const { build } = loadingJobSpec;

    this.fileId = build.file.id;
    this.rfqApp.jobSpec.quantity = loadingJobSpec.quantity;
    this.rfqApp.jobSpec.leadTime = loadingJobSpec.leadTime;
    this.rfqApp.jobSpec.build.supportingFiles = build.supportingFiles;

    const validateRfqInputManager = new ValidateRFQInputManager(this);

    const isClonedDataValid = await validateRfqInputManager.isRfqInputValid(
      loadingJobSpec,
    );

    if (isClonedDataValid) {
      this.rfqApp.jobSpec.build.process = build.process;
      this.rfqApp.jobSpec.build.material = build.material;
      this.rfqApp.jobSpec.build.values = build.values;
    } else {
      Alert.error(
        'Sorry, the copied Process and Material are no longer available, please manually fill the details.',
      );
    }
  };

  initFileRequirementsReaction = () => {
    const disposer = autorun(
      () => {
        const { build } = this.rfqApp?.jobSpec;
        if (!build) return;
        const supportingFileIds = build.supportingFiles?.map(
          supportingFile => supportingFile.id,
        );

        const optionValues = this.rfqApp.jobSpec?.build.valuesTarget;

        const buildValueInputs = Object.values(optionValues)
          .filter(
            optVal => !(optVal === null || optVal.option.name === 'Lead Time'),
          )
          .map(optVal => {
            const buildVal = {
              optionId: optVal.option.id,
            };
            buildVal[optVal.option.valueKey] = optVal.value;
            return buildVal;
          });

        const input = {
          fileId: this.fileId,
          materialId: build.material?.id,
          processId: build.process?.id,
          buildValueInputs,
          supportingFileIds,
        };
        if (!input.fileId || !input.materialId || !input.processId) {
          this.rfqApp.fileRequirements = [];
          this.rfqApp.areFileRequirementsValidatedByServer = false;
          return;
        }
        this.QueryFileRequirements(input);
      },
      { delay: 100 },
    );

    this.disposers.push(disposer);
  };

  initRFQFormSerializationReaction() {
    // Only serialize for signed out users
    if (clientStore.signedInProfile) return;

    const disposer = reaction(
      () => ({
        process: this.rfqApp.jobSpec.build.process,
        material: this.rfqApp.jobSpec.build.material,
        quantity: this.rfqApp.jobSpec.quantity,
        values: Object.values(this.rfqApp.jobSpec.build.valuesTarget),
      }),
      throttle(1000, this.saveRFQFormToStorage),
    );

    this.disposers.push(disposer);
  }

  @computed get submittingRfqForm() {
    return this.jobSpecManager.submitting;
  }

  @action Submit = () => {
    // - we can use the formatObject to determine for what viewer the file is for
    const { formatObject } = getFormatObject(this.file);
    // - files for the GltfViewer need to be checked if we processed them
    // - they can fail to be processed, but we need them to either fail or
    // succeed to continue with the rfq submit process
    const shouldHaveAConversion = formatObject.viewer === 'GltfViewer';
    if (
      // - no file means not created in flask yet
      !this.file ||
      (shouldHaveAConversion &&
        // - files that should have a conversion will block if either
        // conversionGltfjson was not created yet (celery down or task
        // did not run yet)
        (!this.file.conversionGltfjson ||
          // - or their conversion is in status === 'PENDING'
          this.file.conversionGltfjson.status === 'PENDING'))
    ) {
      Alert.error(
        i18next.t(
          'File not processed yet. Please wait a moment or refresh the page.',
        ),
      );
      return;
    }

    if (this.jobSpecManager.isValid) {
      if (!clientStore.signedInProfile) {
        sessionStorage.setItem(
          'pendingRFQSubmission',
          routerStore.route.params.sharedFileId,
        );
        modalStore.SetModalComponent(SignUpModal, {}, () => {
          // modal closed
          if (clientStore.signedInProfile) {
            this.SubmitRFQ();
          }
        });
      } else {
        this.SubmitRFQ();
      }
    } else {
      this.jobSpecManager.alert();
    }
  };

  @action SubmitRFQ() {
    sessionStorage.removeItem('pendingRFQSubmission');
    this.jobSpecManager.SetPosting(true);

    const optionValues = this.rfqApp.jobSpec?.build.valuesTarget;
    let leadTime;
    Object.keys(optionValues).forEach(optKey => {
      const val = optionValues[optKey];
      if (val?.option.name === 'Lead Time') {
        leadTime = val.value;
      }
    });
    const buildValueInputs = Object.values(optionValues)
      .filter(
        optVal => !(optVal === null || optVal.option.name === 'Lead Time'),
      )
      .map(optVal => {
        const buildVal = {
          optionId: optVal.option.id,
        };
        buildVal[optVal.option.valueKey] = optVal.value;
        return buildVal;
      });

    let partId = routerStore.params.get('partId');
    const cadId = routerStore.params.get('cadId');
    if (!partId && cadId) {
      // - I don't like this, the reason I do this hack is because I don't really understand
      // the schema for these ids yet, cadId is more universal, it contains a "path" of indices
      // and then the partId, like 1/2/3-partIdBase64, the nice thing about this format is that
      // it is unique, the partId itself can appear multiple times, but the indices tell me what
      // instance of partId I am looking at, so that is why I like to use it
      // - but here the part extraction code only cares about the partId, not the indices, and
      // since I don't want to change that code I do this split thing here, same way as in
      // viewerControl.cadItemByPartId
      // - I can get the partId from cadItem.GetBody().uuid instead, but this seems safer actually,
      // because the user should already see whatever is currently inspected by cadId/partId, the
      // code below just sends off the request to extract what the user sees, it does not really
      // use cadItem for that, if we did use cadItem.GetBody().uuid then that might fail because
      // cadItem is not a product or whatever, and then has no GetBody, so doing it like this is
      // actually more reliable I think
      const splitId = cadId.split('-');
      if (splitId.length > 1) {
        [, partId] = splitId;
      }
    }

    const supportingFileIds = this.rfqApp.jobSpec?.build.supportingFiles?.map(
      supportingFile => supportingFile.id,
    );

    const rfqInputOptions = {
      jobSpec: {
        build: {
          fileId: this.fileId,
          materialId: this.rfqApp.jobSpec?.build?.material?.id,
          processId: this.rfqApp.jobSpec?.build?.process?.id,
          buildValueInputs,
          supportingFileIds,
        },
        leadTime,
        quantity: this.rfqApp.jobSpec?.quantity,
      },
      addressId: this.rfqApp.address?.id,
    };

    if (!partId) {
      // If it is a standard rfq (i.e. no extraction functionality is required)
      const rfqInput = new Input({
        input: rfqInputOptions,
        spaceId: clientStore.activeSpaceId,
      });
      const rfqMutation = new CreateRFQMutation(rfqInput, null, {}, true);
      rfqMutation.setCallbacks({
        onSysErr: this.onRfqCreationErr,
        onUserErr: this.onRfqCreationErr,
        onSuccess: this.onRfqCreationSuccess,
      });
      rfqMutation.mutate();
    } else {
      const names = [];
      const rootItem = this.viewerControl?.cadFile?.assembly?.GetProduct(0);
      if (rootItem?.name) {
        names.push(rootItem.name);
      }
      const cadItem = this.viewerControl?.cadItemByPartId(partId);
      if (cadItem?.name) {
        names.push(cadItem.name);
      }
      let partFilename = '';
      if (names.length > 0) {
        const randomSlug = Math.random().toString(36).slice(-5);
        names.push(randomSlug);
        partFilename = names.join('-').replace(/[ ./\\]/g, '_');
      }
      const partExtractionManager = new PartExtractionManager(null, {
        fileId: this.fileId,
        partFilenames: [partFilename],
        partId,
        creatorId: clientStore.signedInProfile.user.id,
        rfqInput: rfqInputOptions,
        spaceId: clientStore.activeSpaceId,
        jobSpecManager: this.jobSpecManager,
      });
      partExtractionManager.StartPartExtraction();
    }

    const eventContent = {
      leadTime,
      quantity: this.rfqApp.jobSpec?.quantity,
      material: this.rfqApp.jobSpec?.build?.material?.name,
      process: this.rfqApp.jobSpec?.build?.process?.name,
    };
    // HubSpot?
    if (routerStore.route.name.includes('shared')) {
      // route profile.shared.file
      analytics.sendEvent('share-file-submitted-rfq', eventContent, {
        ga: true,
      });
    } else {
      // route make-part.file
      analytics.sendEvent('make-part-submitted-rfq', eventContent, {
        ga: true,
      });
    }
    analytics.sendEvent(
      'Lead',
      { ...eventContent, currency: 'EUR', value: 100 },
      {
        facebook: true,
      },
    );
  }

  @action onRfqCreationErr = err => {
    this.jobSpecManager.SetPosting(false);
    // TODO: we should maybe use a custom error class here
    analytics.logError(err);
    Alert.error(
      i18next.t('Hmm... We failed to submit your RFQ. Please try again.'),
    );
  };

  @action onRfqCreationSuccess = result => {
    this.jobSpecManager.SetPosting(false);
    Alert.success(i18next.t('RFQ submitted successfully.'));
    routerStore.router.navigate('rfq', {
      rfqId: result.id,
    });
  };

  // duplicated code in RFQAppManager
  @action uploadFiles = (
    files,
    uploadCallback,
    cancelledCallback,
    options = { immadiate: false },
  ) => {
    const { immediate } = options;

    files.forEach(file => {
      const fileManager = new FileAppManager(null, {});
      if (immediate) {
        clientStore.uploader.FastTrackFile(
          fileManager,
          file,
          uploadCallback,
          cancelledCallback,
        );
      } else {
        clientStore.uploader.QueueFile(
          fileManager,
          file,
          uploadCallback,
          cancelledCallback,
        );
      }
    });
  };

  @action AddLocalSupportingFiles = files => {
    this.rfqApp.areFileRequirementsValidatedByServer = false;
    this.rfqApp.jobSpec.build.supportingFilesBeingAdded = [...files];
    const uploadCallback = uploadedFile => {
      if (!this.rfqApp.jobSpec.build.supportingFiles)
        this.rfqApp.jobSpec.build.ResetSupportingFiles();
      this.rfqApp.jobSpec.build.supportingFiles.push(uploadedFile);
      this.rfqApp.jobSpec.build.RemoveSupportingFileBeingAdded(uploadedFile);
    };

    const cancelledCallback = cancelledFile => {
      this.rfqApp.jobSpec.build.RemoveSupportingFileBeingAdded(cancelledFile);
    };

    this.uploadFiles(files, uploadCallback, cancelledCallback);
  };

  @action RemoveLocalSupportingFile = supportingFileId => {
    const { supportingFiles } = this.rfqApp.jobSpec.build;
    if (!supportingFiles) return;
    this.rfqApp.areFileRequirementsValidatedByServer = false;
    this.rfqApp.jobSpec.build.SetSupportingFiles(
      supportingFiles.filter(file => file.id !== supportingFileId),
    );
  };

  @action QueryFileRequirements = input => {
    const q = new FileRequirementsQuery(this, {
      queryKey: 'fileRequirements',
      variables: {
        build: input,
      },
      onSysErr: () => {},
    });
    q.process(fileRequirements => {
      if (fileRequirements) {
        this.rfqApp.fileRequirements = fileRequirements;
        this.rfqApp.areFileRequirementsValidatedByServer = true;
      }
    });
    q.query();
  };

  saveRFQFormToStorage = () => {
    try {
      const shareFileId = routerStore.route.params.sharedFileId;
      if (!shareFileId) return;
      const js = this.rfqApp.jobSpec.toJs();
      const rfqFormStorageKey = `rfq-form-state-${shareFileId}`;
      toSessionStorage(rfqFormStorageKey, js, true);
    } catch (error) {
      analytics.logError(error);
      analytics.sendEvent(
        'Failed RFQForm serialization in saveRFQFormToStorage',
        {
          error,
        },
        { ga: true },
      );
    }
  };

  @action LoadRFQFormFromStorage = () => {
    try {
      const shareFileId = routerStore.route.params.sharedFileId;
      if (!shareFileId) return;
      const rfqFormStorageKey = `rfq-form-state-${shareFileId}`;
      const js = fromSessionStorage(rfqFormStorageKey);
      if (!js) return;
      js.id = `fromStorageJobSpecId-${shareFileId}`;
      js.build.id = `fromStorageBuildId-${shareFileId}`;

      const valueTargetModels = {};
      Object.keys(js.build.valuesTarget).forEach(key => {
        if (!js.build.valuesTarget[key]) return;
        valueTargetModels[key] = OptionValueModel.Load(
          js.build.valuesTarget[key],
        );
      });

      const jobSpecModel = JobSpecModel.Load(js);

      this.AppRef('loadingJobSpec', JobSpecApp);
      this.loadingJobSpec = jobSpecModel;

      this.rfqApp.jobSpec.build.process = this.loadingJobSpec.build.process;
      this.rfqApp.jobSpec.build.material = this.loadingJobSpec.build.material;
      this.rfqApp.jobSpec.quantity = this.loadingJobSpec.quantity;
      this.rfqApp.jobSpec.build.valuesTarget = valueTargetModels;
    } catch (error) {
      analytics.logError(error);
      analytics.sendEvent(
        'Failed RFQForm deserialization in LoadRFQFormFromStorage',
        {
          error,
        },
        { ga: true },
      );
    }
  };
}
